import { trackChurnZeroEvent } from "lib/churn-zero";
import { hasCompanyAccess } from "helpers/access-control";
import { computeTimeBetweenTwoDates } from "helpers/dates";
import { baseActionEpic, IAction, IRequestAction, IRequestDataAction, progressEpic } from "epics/base-epic";
import { CONTACTS } from "constants/action-types";
import { ContactsEvents } from "constants/events";
import siteConfig from "site-config";
import { replace, push } from "react-router-redux";
import { IContact, IBuyer, ICompany, ICompanyApi, IKeys } from "components/contacts/contacts-types";
import { IHeader } from "constants/table-types";
import { format } from "lib/globalize-util";
import { add as snackbarAdd } from "../actions/snackbars";
import { IStore } from "../reducers";
import _ from "lodash";
import iassign from "immutable-assign-v1";
import { updateContacts } from "../actions/contacts-actions";
import me from "models/me";

interface IApiActionResponse {
    success: boolean;
    data: any;
    api: ICompanyApi;
}

export const fetchAccountsEpic = progressEpic<IRequestAction, IContact[]>(
    CONTACTS.ACCOUNTS.FETCH,
    ({ payload }) => ({ method: "GET", url: siteConfig.api + payload.url }),
    {
        dataCustomizer: (response: IContact[], action, store) => {
            const useCompanyAccessControl = store?.scope?.brand?.hasPreference("company_access_control");
            if (me.isBuyer() && useCompanyAccessControl) {
                response = _.filter(response, (contact) => hasCompanyAccess(contact));
            }
            return action.payload.next ? store.contacts.accounts.payload.concat(response) : response;
        },
    }
);

export const fetchBuyerEpic = progressEpic<IRequestAction, IBuyer>(CONTACTS.BUYER.FETCH, ({ payload }) => ({
    method: "GET",
    url: siteConfig.api + payload.url,
}));

export const fetchCompanyEpic = progressEpic<IRequestAction, ICompany>(CONTACTS.COMPANY.FETCH, ({ payload }) => ({
    method: "GET",
    url: siteConfig.api + payload.url,
}));

export const deleteBuyerEpic = baseActionEpic<IRequestAction<{ contact: string }>>(
    CONTACTS.BUYER.DELETE,
    ({ payload }) => ({
        url: siteConfig.api + payload.url,
        method: "DELETE",
    }),
    ({ response }, action, store) => {
        const actions: IAction<unknown>[] = [snackbarAdd({ message: format("Buyer successfully deleted.") })];

        if (store.contacts.accounts.payload) {
            const newContacts = store.contacts.accounts.payload.map((contact) =>
                contact.buyer?.ref === response.user && contact._id === action.payload.meta.contact
                    ? _.omit(contact, "buyer")
                    : contact
            );
            actions.push(updateContacts(newContacts));
        }

        if (store.contacts.company.payload?._id === action.payload.meta.contact) {
            const connections = _.reject(store.contacts.company.payload.user_connections, { ref: response.user });
            actions.push({
                type: CONTACTS.COMPANY.UPDATE.SUCCESS,
                payload: { ...store.contacts.company.payload, user_connections: connections },
            });
        }

        if (action.payload.redirect) {
            actions.push(replace(`/brand/${store.scope.brand._id}/contacts`));
        }

        return actions;
    }
);

export const deleteCompanyEpic = baseActionEpic<IRequestAction>(
    CONTACTS.COMPANY.DELETE,
    ({ payload }) => ({
        url: siteConfig.api + payload.url,
        method: "DELETE",
    }),
    ({ response: company }, action, store) => {
        const actions: IAction<unknown>[] = [
            snackbarAdd({ message: format("Company |name| successfully deleted.", { name: company.name }) }),
            { type: CONTACTS.COMPANY.UPDATE.SUCCESS, payload: null },
        ];

        if (store.contacts.accounts.payload) {
            const newContacts = _.filter(store.contacts.accounts.payload, (contact) => contact._id !== company._id);
            actions.push(updateContacts(newContacts));
        }

        if (action.payload.redirect) {
            actions.push(replace(`/brand/${store.scope.brand._id}/contacts`));
        }

        return actions;
    }
);

export const createCompanyEpic = baseActionEpic<IRequestDataAction<ICompany>>(
    CONTACTS.COMPANY.CREATE,
    ({ payload }) => ({
        url: siteConfig.api + payload.url,
        method: "PUT",
        body: payload.body,
    }),
    ({ response: company }, action, store) => {
        trackChurnZeroEvent({
            name: ContactsEvents.CompanyCreated,
            customFields: {
                brandId: store.scope.brand._id,
                companyId: company._id,
                companyCreatedDate: company.created_on,
            },
        });

        return [
            snackbarAdd({ message: format("Company |name| has been created.", { name: company.name }) }),
            push(`/brand/${store.scope.brand._id}/contacts/company/${company._id}`),
        ];
    },
    { checkErrorStatus }
);

export const createBuyerEpic = baseActionEpic<IRequestDataAction<IBuyer>>(
    CONTACTS.BUYER.CREATE,
    ({ payload }) => ({
        url: siteConfig.api + payload.url,
        method: "PUT",
        body: payload.body,
    }),
    ({ response }, action, store) => {
        action.meta?.onSuccess?.();

        const timeInHoursBetweenBuyerAddAndCompanyCreate = computeTimeBetweenTwoDates(
            response.companyRef.created_on,
            response.companyEntry.buyer_added_on,
            "hours"
        );

        trackChurnZeroEvent({
            name: ContactsEvents.BuyerAddedToCompany,
            customFields: {
                brandId: store.scope.brand._id,
                companyId: response.companyRef._id,
                buyerId: response.user._id,
                companyBuyerId: response.companyEntry._id,
                companyCreatedDate: response.companyRef.created_on,
                buyerAddedDate: response.companyEntry.buyer_added_on,
                timeBetweenBuyerAddAndCompanyCreate: timeInHoursBetweenBuyerAddAndCompanyCreate,
            },
        });

        return [
            snackbarAdd({ message: format("|name| buyer's account has been created.", { name: response.user.name }) }),
            push(`/brand/${store.scope.brand._id}/contacts/buyer/${response.companyEntry._id}`),
        ];
    },
    { checkErrorStatus }
);

export const updateBuyerEpic = progressEpic<IRequestDataAction<IContact, { company: string }>, IContact>(
    CONTACTS.BUYER.UPDATE,
    ({ payload }) => ({
        url: siteConfig.api + payload.url,
        method: "POST",
        body: payload.body,
    }),
    {
        dataCustomizer: (buyer, action, store: IStore) => {
            return iassign<IContact>(store.contacts.buyer.payload, (contact) => _.assign(contact, { buyer }));
        },
        finishActions: (buyer, action, store: IStore) => {
            const actions: IAction<unknown>[] = [
                snackbarAdd({ message: format("|name| buyer has been updated.", { name: buyer.name }) }),
            ];

            if (store.contacts.accounts.payload) {
                const newContacts = store.contacts.accounts.payload.map((contact) =>
                    contact.buyer?._id === buyer._id ? { ...contact, buyer } : contact
                );
                actions.push(updateContacts(newContacts));
            }

            if (store.contacts.company.payload?._id === action.payload.meta.company) {
                const connections = store.contacts.company.payload.user_connections.map((connection) =>
                    connection._id === buyer._id ? { ...connection, ...buyer } : connection
                );
                actions.push({
                    type: CONTACTS.COMPANY.UPDATE.SUCCESS,
                    payload: { ...store.contacts.company.payload, user_connections: connections },
                });
            }

            return actions;
        },
        checkErrorStatus,
    }
);

export const updateCompanyEpic = progressEpic<IRequestDataAction<ICompany>, ICompany>(
    CONTACTS.COMPANY.UPDATE,
    ({ payload }) => ({
        url: siteConfig.api + payload.url,
        method: "POST",
        body: payload.body,
    }),
    {
        finishActions: (company, action, store) => {
            const actions: IAction<unknown>[] = [
                snackbarAdd({ message: format("|name| company has been updated.", { name: company.name }) }),
            ];

            if (store.contacts.accounts.payload) {
                const newContacts = store.contacts.accounts.payload.map((contact) =>
                    contact._id === company._id
                        ? { ..._.omit(company, "user_connections"), buyer: contact.buyer }
                        : contact
                );
                actions.push(updateContacts(newContacts));
            }

            return actions;
        },
        checkErrorStatus,
    }
);

export const generateKeysEpic = baseActionEpic<IRequestDataAction<IKeys>>(
    CONTACTS.COMPANY.API.GENERATE_KEYS,
    ({ payload }) => ({ url: siteConfig.api + payload.url, method: "GET" }),
    ({ response }, action, store: IStore) => {
        const keys: IKeys = response.data;
        return [
            {
                type: CONTACTS.COMPANY.API.UPDATE,
                payload: iassign<ICompanyApi>(store.contacts.api.payload, (api) =>
                    _.assign(api, { consumer_key: keys.consumer_key, consumer_secret_key: keys.consumer_secret_key })
                ),
            },
        ];
    }
);

export const getCompanyApiEpic = baseActionEpic<IRequestDataAction<ICompany>>(
    CONTACTS.COMPANY.API.GET,
    ({ payload }) => ({ url: siteConfig.api + payload.url, method: "GET" }),
    ({ response }) => {
        const api = _.chain(response)
            .get("modules.api")
            // @ts-ignore
            .pick(["consumer_key", "consumer_secret_key", "tokens"])
            .defaults({
                consumer_key: "",
                consumer_secret_key: "",
                tokens: [],
            })
            .value();
        return [
            {
                type: CONTACTS.COMPANY.API.UPDATE,
                payload: api,
            },
        ];
    }
);

export const approveAppEpic = baseActionEpic<IRequestDataAction<IApiActionResponse>>(
    CONTACTS.COMPANY.API.APPROVE,
    ({ payload }) => ({ url: siteConfig.api + payload.url, method: "GET" }),
    ({ response }) => {
        if (response.data.callback) {
            return [
                snackbarAdd({ message: format("Successfully approved application.") }),
                {
                    type: CONTACTS.COMPANY.API.UPDATE,
                    payload: response.api,
                },
            ];
        } else {
            return [
                snackbarAdd({
                    message: format("Provide the following verification code to the external application: |verifier|", {
                        verifier: response.data.verifier,
                    }),
                }),
            ];
        }
    }
);

export const fetchHeadersEpic = progressEpic<IRequestAction, IHeader[]>(CONTACTS.HEADERS.FETCH, ({ payload }) => ({
    method: "GET",
    url: siteConfig.api + payload.url,
}));

export const updateHeadersEpic = baseActionEpic<IRequestDataAction<IHeader[]>>(
    CONTACTS.HEADERS.UPDATE,
    ({ payload }) => ({ method: "PUT", url: siteConfig.api + payload.url, body: payload.body }),
    () => [],
    { debounce: 1000 }
);

export const disableAppEpic = baseActionEpic<IRequestDataAction<IApiActionResponse>>(
    CONTACTS.COMPANY.API.DISABLE,
    ({ payload }) => ({ url: siteConfig.api + payload.url, method: "GET" }),
    ({ response }) => {
        return [
            snackbarAdd({ message: format("Successfully disabled application.") }),
            {
                type: CONTACTS.COMPANY.API.UPDATE,
                payload: response.api,
            },
        ];
    }
);

function checkErrorStatus({ xhr }) {
    if (xhr.response && xhr.response.localizedMessage) {
        throw Error(xhr.response.localizedMessage);
    } else {
        throw Error(format("Oops! Something went wrong. Please refresh and try again."));
    }
}
